import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Eye, Loader2, Printer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClearableSelect from "@/components/ClearableSelect";
import { MODULI, PBox, Fld, Sw } from "@/components/StampaMandatoDialog";

// Sprint 17 — moduli in bianco da compilare a mano. Niente salvataggio, niente archivio: solo anteprima.
const MODULI_BIANCO = ["mandato_conto_terzi", "presa_carico", "allegato_privacy"];
const PERIODI = [["giorno", "Al giorno"], ["mese", "Al mese"], ["forfettario", "Forfettario"]];
const NOTA_ART10 = "Lasciala attiva se non sai chi firmerà. Se il Preponente è un privato, l'articolo è dovuto per legge: ometterlo non fa decorrere il termine di recesso. Toglila solo quando sei certo che il Preponente non sia un consumatore.";
const CLAUSOLE_INIZIALI = { esclusiva: true, deposito_attivo: false, firma_fuori_locali: true, compenso_tipo: "", percentuale: "" };

const unico = (lista) => (lista.length === 1 ? lista[0].id : "");

function Passi({ step }) {
  return (
    <div className="flex items-center gap-2 px-5 py-2 border-b bg-slate-50 text-xs" data-testid="bianco-steps">
      {[["1", "Scelta del documento"], ["2", "Parametri e anteprima"]].map(([n, t], i) => (
        <React.Fragment key={n}>
          {i > 0 && <span className="w-6 h-px bg-slate-300 mx-1" />}
          <span className={`flex items-center gap-1.5 ${String(step) === n ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
            <b className={`w-[19px] h-[19px] rounded-full text-white text-[11px] flex items-center justify-center ${String(step) === n ? "bg-[#0F4C81]" : "bg-slate-300"}`}>{n}</b>{t}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function SceltaModulo({ onSelect }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }} data-testid="bianco-elenco-moduli">
      {MODULI_BIANCO.map((m) => (
        <button key={m} type="button" onClick={() => onSelect(m)} data-testid={`bianco-modulo-${m}`}
                className="text-left rounded-md border border-slate-200 bg-white px-3 py-2 flex flex-col gap-0.5 transition-colors duration-150 hover:border-[#0F4C81] hover:bg-sky-50">
          <span className="text-[13px] font-medium text-slate-900 leading-tight">{MODULI[m].label}</span>
          <span className="text-[11px] text-slate-500">In bianco, da compilare a mano</span>
        </button>
      ))}
    </div>
  );
}

function Num({ value, onChange, testid, min }) {
  return <Input type="number" min={min} value={value ?? ""} data-testid={testid}
                onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
}

function ParametriMandato({ p, set }) {
  return (
    <>
      <PBox title="CLAUSOLE" testid="bianco-box-clausole">
        <Sw label="Esclusiva (art. 1.4)" checked={p.esclusiva} onChange={set("esclusiva")} testid="bianco-esclusiva" />
        <Sw label="Deposito e custodia (art. 3)" checked={p.deposito_attivo} onChange={set("deposito_attivo")} testid="bianco-deposito" />
        <Sw label="Firma fuori dai locali (art. 10)" hint={NOTA_ART10} checked={p.firma_fuori_locali}
            onChange={set("firma_fuori_locali")} testid="bianco-fuori-locali" />
      </PBox>
      <PBox title="COMPENSO" testid="bianco-box-compenso">
        <Fld label="Tipo di compenso">
          <ClearableSelect value={p.compenso_tipo} onChange={(v) => set("compenso_tipo")(v || "")} clearable={false}
                           options={[["percentuale", "A percentuale"], ["differenza", "A differenza"]]}
                           placeholder="Scegli…" testId="bianco-compenso-tipo" />
        </Fld>
        {p.compenso_tipo === "percentuale" && (
          <Fld label="Percentuale (art. 4.3)"><Num value={p.percentuale} onChange={set("percentuale")} testid="bianco-percentuale" /></Fld>
        )}
        <Fld label="Compenso per vendita diretta (€)" hint={p.compenso_tipo === "differenza" ? "A differenza, senza importo l'art. 4.6 non si stampa." : undefined}>
          <Num value={p.vendita_diretta} onChange={set("vendita_diretta")} testid="bianco-vendita-diretta" />
        </Fld>
      </PBox>
      <PBox title="PARAMETRI CONTRATTUALI" testid="bianco-box-parametri">
        <Fld label="Penale per revoca (€)" hint="Si stampa solo con l'esclusiva."><Num value={p.penale_revoca} onChange={set("penale_revoca")} testid="bianco-penale" /></Fld>
        <Fld label="Giorni di disdetta"><Num value={p.giorni_disdetta} onChange={set("giorni_disdetta")} testid="bianco-giorni-disdetta" /></Fld>
        <Fld label="Giorni di recesso fuori sede" hint="Minimo 14."><Num min={14} value={p.giorni_recesso_fuori} onChange={set("giorni_recesso_fuori")} testid="bianco-giorni-recesso" /></Fld>
        <Fld label="Costo del deposito (€)" dim={!p.deposito_attivo}><Num value={p.costo_deposito} onChange={set("costo_deposito")} testid="bianco-costo-deposito" /></Fld>
        <Fld label="Periodo del deposito" dim={!p.deposito_attivo}>
          <ClearableSelect value={p.periodo_deposito || ""} onChange={(v) => set("periodo_deposito")(v || null)}
                           options={PERIODI} placeholder="—" testId="bianco-periodo-deposito" />
        </Fld>
      </PBox>
    </>
  );
}

async function messaggioErrore(e, fallback) {
  let d = e?.response?.data;
  if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch { d = null; } }
  const det = d?.detail;
  return typeof det === "string" ? det : det?.messaggio || fallback;
}

export default function StampeInBianco() {
  const [step, setStep] = useState(1);
  const [modulo, setModulo] = useState(null);
  const [aziende, setAziende] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [p, setP] = useState(CLAUSOLE_INIZIALI);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    api.get("/stampe/in-bianco/aziende").then((r) => setAziende(r.data?.items || [])).catch(() => setAziende([]));
  }, []);
  const azienda = useMemo(() => aziende.find((a) => a.id === companyId), [aziende, companyId]);
  const sedi = azienda?.sedi || [];

  const scegliAzienda = (cid) => {
    const a = aziende.find((x) => x.id === cid);
    setCompanyId(cid || ""); setSedeId(unico(a?.sedi || [])); setErrore(null);
    setP({ ...CLAUSOLE_INIZIALI, ...(a?.parametri || {}) });
  };
  const apri = (m) => { setModulo(m); setStep(2); setErrore(null); scegliAzienda(unico(aziende)); };
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));

  const manca = !companyId ? "Scegli l'azienda" : !sedeId ? "Scegli la sede"
    : modulo === "mandato_conto_terzi" && !p.compenso_tipo ? "Scegli il tipo di compenso" : null;

  const anteprima = async () => {
    setBusy(true); setErrore(null);
    try {
      const params = modulo === "mandato_conto_terzi" ? p : {};
      const r = await api.post(`/stampe/in-bianco/${modulo}/preview`, { company_id: companyId, sede_id: sedeId, params }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = await messaggioErrore(e, "Errore durante l'anteprima");
      setErrore(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="stampe-in-bianco-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 flex items-center gap-2"><Printer className="h-5 w-5" /> Stampe</h1>
        <p className="text-sm text-slate-500">Moduli in bianco da compilare a mano. Nulla di ciò che imposti qui viene salvato o archiviato.</p>
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden max-w-4xl">
        <div className="px-5 py-3 border-b text-base font-semibold flex items-center gap-2" data-testid="bianco-titolo">
          <Printer className="h-4 w-4" /> {step === 2 && modulo ? `Modulo in bianco — ${MODULI[modulo].label}` : "Modulo in bianco"}
        </div>
        <Passi step={step} />
        <div className="px-5 py-4 space-y-3">
          {step === 1 ? <SceltaModulo onSelect={apri} /> : (
            <>
              {errore && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-1" data-testid="bianco-errore">
                  <AlertTriangle className="h-4 w-4" /> {errore}
                </div>
              )}
              <PBox title="AZIENDA E SEDE" testid="bianco-box-azienda">
                <Fld label="Azienda *">
                  <ClearableSelect value={companyId} onChange={scegliAzienda} clearable={false} placeholder="Scegli l'azienda…"
                                   options={aziende.map((a) => [a.id, a.nome])} testId="bianco-azienda" />
                </Fld>
                <Fld label="Sede operativa *" hint="Determina sede operativa, telefono ed email in intestazione.">
                  <ClearableSelect value={sedeId} onChange={(v) => setSedeId(v || "")} clearable={false} placeholder={companyId ? "Scegli la sede…" : "Prima l'azienda"}
                                   options={sedi.map((s) => [s.id, s.nome])} testId="bianco-sede" />
                </Fld>
              </PBox>
              {modulo === "mandato_conto_terzi" && companyId && <ParametriMandato p={p} set={set} />}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 px-5 py-3 border-t bg-slate-50" data-testid="bianco-azioni">
          {step === 1 ? <span className="text-[11.5px] text-slate-500">Scegli un documento per continuare.</span> : (
            <>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="bg-transparent border-[#0F4C81] text-[#0F4C81] hover:bg-transparent hover:text-[#0c3d68]" data-testid="bianco-indietro-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Cambia documento
              </Button>
              <span className="ml-auto text-[11.5px] text-slate-500" data-testid="bianco-manca">{manca || "Solo anteprima: nessun documento viene archiviato"}</span>
              <Button onClick={anteprima} disabled={busy || !!manca} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bianco-preview-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Anteprima
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
